import { snapshotFromStore } from '../stores/undoStore';
import { useDiagramStore } from '../stores/diagramStore';
import { useSettingsStore } from '../stores/settingsStore';
import { colorService } from '../services/color';

/** Ten named presets for `c then c then 0–9`. */
export const COLOR_PRESETS: { hex: string; name: string }[] = [
  { hex: '#94a3b8', name: 'Slate' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#eab308', name: 'Yellow' },
  { hex: '#22c55e', name: 'Green' },
  { hex: '#14b8a6', name: 'Teal' },
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#a855f7', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
];

export function colorPresetAt(digit: number): { hex: string; name: string } | null {
  if (!Number.isInteger(digit) || digit < 0 || digit > 9) return null;
  return COLOR_PRESETS[digit] ?? null;
}

export function withStickyDrawColor<T extends { color?: string }>(data: T): T {
  const settings = useSettingsStore.getState();
  if (!settings.stickyDrawColorEnabled || !settings.lastDrawColor) return data;
  return { ...data, color: settings.lastDrawColor };
}

export function rememberDrawColor(color: string) {
  if (!colorService.isValidHex(color)) return;
  useSettingsStore.getState().setLastDrawColor(color);
  colorService.addRecentColor(color);
}

/** Paint the current selection (nodes, edges, frames, sequence). Returns how many items changed. */
export function applyColorToSelection(color: string): number {
  if (!colorService.isValidHex(color)) return 0;
  const st = useDiagramStore.getState();
  const nodeIds = st.selectedItems.filter(id => st.nodes.some(n => n.id === id));
  const edgeIds = st.selectedItems.filter(id => st.edges.some(e => e.id === id));
  const groupIds = st.selectedItems.filter(id => st.groups.some(g => g.id === id && g.isFrame));
  const participantIds = st.selectedItems.filter(id => st.participants.some(p => p.id === id));
  const messageIds = st.selectedItems.filter(id => st.messages.some(m => m.id === id));
  const count = nodeIds.length + edgeIds.length + groupIds.length + participantIds.length + messageIds.length;
  if (count === 0) {
    rememberDrawColor(color);
    return 0;
  }
  snapshotFromStore();
  for (const id of nodeIds) {
    const node = st.nodes.find(n => n.id === id);
    if (node) st.updateNode(id, { data: { ...node.data, color } });
  }
  for (const id of edgeIds) st.updateEdge(id, { color });
  for (const id of groupIds) st.updateGroup(id, { color });
  for (const id of participantIds) st.updateParticipant(id, { color });
  for (const id of messageIds) st.updateMessage(id, { color });
  rememberDrawColor(color);
  return count;
}

export interface ColorPickerPayload {
  show: true;
  targetNodeIds: string[];
  targetEdgeIds?: string[];
  targetGroupIds?: string[];
  targetParticipantIds?: string[];
  targetMessageIds?: string[];
  avoidRect?: { x: number; y: number; w: number; h: number };
}

export function colorPickerPayloadForSelection(
  viewport: { x: number; y: number; scale: number },
  cursor: { x: number; y: number },
): { picker: ColorPickerPayload } | { toast: string } {
  const st = useDiagramStore.getState();
  if (st.diagramType === 'sequence') {
    const hovP = st.participants.find(p => cursor.x >= p.x && cursor.x <= p.x + 140 && cursor.y >= 40 && cursor.y <= 84);
    const hovM = st.messages.find(m => Math.abs(cursor.y - m.y) < 12);
    const selPIds = st.selectedItems.filter(id => st.participants.some(p => p.id === id));
    const selMIds = st.selectedItems.filter(id => st.messages.some(m => m.id === id));
    if (selPIds.length > 0) {
      const firstP = st.participants.find(p => p.id === selPIds[0]);
      const sx = firstP ? firstP.x * viewport.scale + viewport.x : 0;
      const sy = 40 * viewport.scale + viewport.y;
      return {
        picker: {
          show: true, targetNodeIds: [], targetEdgeIds: [], targetParticipantIds: selPIds,
          avoidRect: firstP ? { x: sx, y: sy, w: 140 * viewport.scale, h: 44 * viewport.scale } : undefined,
        },
      };
    }
    if (selMIds.length > 0) {
      return { picker: { show: true, targetNodeIds: [], targetEdgeIds: [], targetMessageIds: selMIds } };
    }
    if (hovP) {
      const sx = hovP.x * viewport.scale + viewport.x;
      const sy = 40 * viewport.scale + viewport.y;
      return {
        picker: {
          show: true, targetNodeIds: [], targetEdgeIds: [], targetParticipantIds: [hovP.id],
          avoidRect: { x: sx, y: sy, w: 140 * viewport.scale, h: 44 * viewport.scale },
        },
      };
    }
    if (hovM) {
      return { picker: { show: true, targetNodeIds: [], targetEdgeIds: [], targetMessageIds: [hovM.id] } };
    }
    return { toast: 'Hover or select a participant/message, then press c+c' };
  }

  const hoveredNode = st.nodes.find(n =>
    cursor.x >= n.position.x && cursor.x <= n.position.x + n.size.width &&
    cursor.y >= n.position.y && cursor.y <= n.position.y + n.size.height
  );
  const nodeToScreen = (n: typeof st.nodes[0]) => ({
    x: n.position.x * viewport.scale + viewport.x,
    y: n.position.y * viewport.scale + viewport.y,
    w: n.size.width * viewport.scale, h: n.size.height * viewport.scale,
  });
  const hoveredFrame = st.groups.find(g =>
    g.isFrame &&
    cursor.x >= g.position.x && cursor.x <= g.position.x + g.size.width &&
    cursor.y >= g.position.y && cursor.y <= g.position.y + g.size.height
  );
  const groupToScreen = (g: typeof st.groups[0]) => ({
    x: g.position.x * viewport.scale + viewport.x,
    y: g.position.y * viewport.scale + viewport.y,
    w: g.size.width * viewport.scale, h: g.size.height * viewport.scale,
  });
  const selGroupIds = st.selectedItems.filter(id => st.groups.some(g => g.id === id && g.isFrame));
  const selNodeIds = st.selectedItems.filter(id => st.nodes.some(n => n.id === id));
  const selEdgeIds = st.selectedItems.filter(id => st.edges.some(ed => ed.id === id));
  if (selGroupIds.length > 0) {
    const firstGroup = st.groups.find(g => g.id === selGroupIds[0]);
    return { picker: { show: true, targetNodeIds: [], targetEdgeIds: [], targetGroupIds: selGroupIds, avoidRect: firstGroup ? groupToScreen(firstGroup) : undefined } };
  }
  if (selNodeIds.length > 0) {
    const firstNode = st.nodes.find(n => n.id === selNodeIds[0]);
    return { picker: { show: true, targetNodeIds: selNodeIds, targetEdgeIds: [], avoidRect: firstNode ? nodeToScreen(firstNode) : undefined } };
  }
  if (selEdgeIds.length > 0) {
    return { picker: { show: true, targetNodeIds: [], targetEdgeIds: selEdgeIds } };
  }
  if (hoveredFrame) {
    return { picker: { show: true, targetNodeIds: [], targetEdgeIds: [], targetGroupIds: [hoveredFrame.id], avoidRect: groupToScreen(hoveredFrame) } };
  }
  if (hoveredNode) {
    return { picker: { show: true, targetNodeIds: [hoveredNode.id], targetEdgeIds: [], avoidRect: nodeToScreen(hoveredNode) } };
  }
  return { toast: 'Select nodes, edges, or frames, then press c+c to change color' };
}
